(function () {
  // ───────────────────────────────────────────────────────────────────────────
  // Unified variant sync (gallery + dimensions image) for DESKTOP and MOBILE.
  //
  // What this fixes (vs. the previous build):
  //   1. The previous build bailed out on desktop (`if (!isMobile()) return`), so
  //      the desktop gallery NEVER swapped. Now it runs everywhere.
  //   2. The previous build read `input[name="id"]` for the selected variant, but
  //      this theme's master variant control is a `<select name="id">` — so that
  //      read returned null and code fell back to the URL `?variant=` param. On a
  //      plain click (URL unchanged) it re-applied the OLD variant → nothing moved.
  //      Now we read `select[name="id"], input[name="id"]` and ALSO watch it with a
  //      lightweight rAF poller, because the theme updates that control via
  //      `.val()` (programmatic — no `change` event), so only a poll catches it
  //      reliably on every selection method (click, swatch, history, deep-link).
  //   3. The previous build relied on `window.productJSON` which does not exist in
  //      this theme. We no longer need it — the selected variant *id* alone is
  //      enough to look up both the per-variant gallery block and the dims map.
  //   4. SINGLE-IMAGE products (only 1 product image) used to be completely broken:
  //      the theme never renders the `.product-single__thumbnails` strip and never
  //      initializes Slick (it guards with `length > 1`), so `bind()` bailed out
  //      early (no `$thumbs`) and `bothInitialized()` was NEVER true → the variant
  //      gallery never swapped in. Now:
  //        • product-media.liquid also renders the (possibly empty) thumb container
  //          when the product has variant galleries / dimensions, so `$thumbs` exists.
  //        • This script no longer waits forever for Slick. If the theme initialized
  //          Slick (multi-image) we reuse its exact options; otherwise (single-image)
  //          we synthesize sane horizontal/vertical defaults and still swap.
  //        • `swapTo` / `captureOriginal` are Slick-agnostic: they unslick only if
  //          Slick is live, and (re)initialize Slick only when there is >1 slide.
  //
  // Trigger chain (all idempotent via lastAppliedId):
  //   • variantChange (theme)             → use e.variant.id, else fall back to the
  //                                          select value.
  //   • change on option controls / select→ read the (already-updated) master select.
  //   • rAF poller on the master select    → catches programmatic .val() updates.
  //   • initial load (DOMContentLoaded/load/poller) → applies the pre-selected variant.
  // ───────────────────────────────────────────────────────────────────────────

  var MAIN = '[class*="gallery-wrap"]';
  var THUMBS = ".product-single__thumbnails";
  var GALLERIES = ".taitiy-variant-galleries";
  var THUMBS_WRAP = ".photos__item--thumbs";
  var ROOT = ".product-single";
  // The theme's master variant control. It is a <select name="id"> (updated via
  // .val() by the theme) — NOT an <input>. We accept both just in case.
  var ID_SELECTOR = 'select[name="id"], input[name="id"]';

  var $ = window.jQuery;
  if (!$ || !$.fn || !$.fn.slick) return;

  var $root, $main, $thumbs, $galleries, $thumbsWrap;
  var mainOptsRaw = null, thumbOptsRaw = null;
  var originalMain = null, originalThumbs = null;
  var currentVariantId = null;   // which per-variant gallery is currently shown (null = default)
  var lastAppliedId = null;      // last variant id we fully applied (idempotency)
  var boundOnce = false;
  var listenersArmed = false;
  var patchedOnce = false;
  var pendingVariantId = null;
  var slickReady = false;
  var wasSlickInit = false;      // true only when the THEME initialized Slick (multi-image)
  var readyRAF = null;
  var applyRAF = null;
  var pollRAF = null;

  function isMobile() {
    return window.matchMedia && window.matchMedia("(max-width: 749px)").matches;
  }

  function slickOn($el) {
    return !!( $el && $el.length && $el.hasClass("slick-initialized") );
  }

  // True only when BOTH galleries are Slick-initialized by the theme (multi-image).
  function bothInitialized() {
    return slickOn($main) && slickOn($thumbs);
  }

  // True when the gallery DOM targets exist (regardless of Slick state). This is the
  // real gate for "can we swap at all".
  function domReady() {
    return !!( $main && $main.length && $thumbs && $thumbs.length && $galleries && $galleries.length );
  }

  // Desktop thumbnails are vertical when the section uses photos-thumb-left/right.
  // That class lives on the .photos ancestor (not on .photos__item--thumbs), so we
  // walk up with .closest().
  function isVerticalLayout() {
    var $scope = ($thumbsWrap && $thumbsWrap.length) ? $thumbsWrap
               : (($root && $root.length) ? $root : null);
    if (!$scope || !$scope.closest) return false;
    return $scope.closest('.photos-thumb-left, .photos-thumb-right').length > 0;
  }

  // ── Slick options ──────────────────────────────────────────────────────────
  // Capture the EXACT options the theme used (multi-image products). On mobile we
  // apply geometry patches (so the 40px thumb strip is exact and never auto-scrolls).
  // On desktop we keep the theme's options verbatim, including the `asNavFor` linkage.
  function captureOpts() {
    try {
      if (!mainOptsRaw && slickOn($main)) {
        mainOptsRaw = $main.slick("getSlick").options;
      }
      if (!thumbOptsRaw && slickOn($thumbs)) {
        thumbOptsRaw = $thumbs.slick("getSlick").options;
      }
    } catch (e) {}
  }

  // Synthesize sane defaults for single-image products where the theme never inits
  // Slick. Direction (vertical/horizontal) matches the section layout.
  function defaultMainOpts() {
    if (isMobile()) {
      return { slidesToShow: 1, slidesToScroll: 1, arrows: false, dots: false, fade: false, adaptiveHeight: false, asNavFor: null };
    }
    return { slidesToShow: 1, slidesToScroll: 1, arrows: false, dots: false, fade: false, adaptiveHeight: false, asNavFor: $thumbs };
  }

  function defaultThumbOpts() {
    if (isMobile()) {
      return { accessibility: true, arrows: false, dots: false, infinite: false, autoplay: false,
               slidesToShow: 4, slidesToScroll: 1, focusOnSelect: true, asNavFor: $main, variableWidth: true };
    }
    if (isVerticalLayout()) {
      return { accessibility: true, arrows: false, dots: false, infinite: false, autoplay: false,
               slidesToShow: 5, slidesToScroll: 1, focusOnSelect: true, asNavFor: $main, vertical: true, verticalSwiping: true };
    }
    return { accessibility: true, arrows: false, dots: false, infinite: false, autoplay: false,
             slidesToShow: 6, slidesToScroll: 1, focusOnSelect: true, asNavFor: $main };
  }

  function ensureDefaults() {
    if (!mainOptsRaw) mainOptsRaw = defaultMainOpts();
    if (!thumbOptsRaw) thumbOptsRaw = defaultThumbOpts();
  }

  function getSwapMainOpts() {
    if (!mainOptsRaw) return null;
    if (isMobile()) {
      var o = $.extend({}, mainOptsRaw);
      o.asNavFor = null; // mobile: we sync thumb↔main manually
      return o;
    }
    return mainOptsRaw;
  }

  function getSwapThumbOpts() {
    if (!thumbOptsRaw) return null;
    if (isMobile()) {
      var o = $.extend({}, thumbOptsRaw);
      o.variableWidth = true;   // measure the REAL 40px width → exact transforms
      o.focusOnSelect = false;  // clicking a thumb must NOT auto-scroll the strip
      o.asNavFor = null;
      return o;
    }
    return thumbOptsRaw;
  }

  function captureOriginal() {
    if (originalMain != null) return;
    var wasInit = slickOn($main) || slickOn($thumbs);
    try {
      if (wasInit) { $main.slick("unslick"); $thumbs.slick("unslick"); }
      originalMain = $main.html();
      originalThumbs = $thumbs.html();
      if (wasInit) {
        var t = getSwapThumbOpts(), m = getSwapMainOpts();
        if (t && $thumbs.children().length > 1) $thumbs.slick(t);
        if (m && $main.children().length > 1) $main.slick(m);
      }
    } catch (e) {
      originalMain = null;
      originalThumbs = null;
    }
  }

  function swapTo(mainHtml, thumbsHtml) {
    if (slickOn($main)) { try { $main.slick("unslick"); } catch (e) {} }
    if (slickOn($thumbs)) { try { $thumbs.slick("unslick"); } catch (e) {} }
    $main.html(mainHtml);
    $thumbs.html(thumbsHtml);
    var t = getSwapThumbOpts(), m = getSwapMainOpts();
    // Only (re)initialize Slick when there is more than one slide; a single image
    // does not need a carousel and the theme leaves it as plain markup.
    if (t && $thumbs.children().length > 1) { try { $thumbs.slick(t); } catch (e) {} }
    if (m && $main.children().length > 1) { try { $main.slick(m); } catch (e) {} }
    requestAnimationFrame(function () { updateCenter(); neutralizeCentered(); setActiveThumb(0); });
  }

  function setActiveThumb(idx) {
    if (!$thumbs.length) return;
    if (idx == null || isNaN(idx)) return;
    $thumbs.find(".product-single__thumbnail").removeClass("is-active");
    var $slide = $thumbs.find('.slick-slide[data-slick-index="' + idx + '"]');
    if ($slide.length) $slide.find(".product-single__thumbnail").addClass("is-active");
  }

  function showGallery(variantId) {
    var $block = $galleries.find('.taitiy-variant-gallery[data-variant-id="' + variantId + '"]');
    if (!$block.length) return false;
    var mainHtml = $block.find(".taitiy-vg-main").html();
    var thumbsHtml = $block.find(".taitiy-vg-thumbs").html();
    if (mainHtml == null || thumbsHtml == null) return false;

    captureOriginal();
    swapTo(mainHtml, thumbsHtml);
    setActiveThumb(0);
    currentVariantId = variantId;
    return true;
  }

  function showDefault() {
    if (originalMain == null || originalThumbs == null) return;
    swapTo(originalMain, originalThumbs);
    setActiveThumb(0);
    currentVariantId = null;
  }

  function updateCenter() {
    if (!isMobile() || !$thumbsWrap || !$thumbsWrap.length) return;
    if (!$thumbs.hasClass("slick-initialized")) return;
    var track = $thumbs.find(".slick-track");
    if (!track.length) return;
    var tw = track[0].scrollWidth;
    var cw = $thumbsWrap[0].clientWidth;
    if (tw <= cw + 1) $thumbsWrap.addClass("is-centered");
    else $thumbsWrap.removeClass("is-centered");
  }

  function neutralizeCentered() {
    if (!isMobile() || !$thumbs || !$thumbs.hasClass("slick-initialized")) return;
    if ($thumbsWrap && $thumbsWrap.hasClass("is-centered")) {
      var tr = $thumbs.find(".slick-track")[0];
      if (tr) tr.style.transform = "translate3d(0,0,0)";
    }
  }

  function armListeners() {
    if (listenersArmed || !$thumbs.length || !$main.length) return;
    listenersArmed = true;
    // Thumb click → switch main + highlight. (On mobile focusOnSelect is false so
    // this is required; on desktop it is harmless because focusOnSelect already does
    // it and slickGoTo to the same index is a no-op.)
    $thumbs.off("click.taitiy").on("click.taitiy", ".slick-slide", function () {
      var idx = parseInt($(this).attr("data-slick-index"), 10);
      if (isNaN(idx)) return;
      try { $main.slick("slickGoTo", idx, false); } catch (e) {}
      setActiveThumb(idx);
    });
    // Main slide change → highlight the matching thumb (both layouts).
    $main.off("afterChange.taitiy").on("afterChange.taitiy", function (e, slick, currentSlide) {
      setActiveThumb(currentSlide);
    });
    setActiveThumb(0);
  }

  // Apply the (mobile) patched options to the LIVE default gallery once, so the
  // strip is correct from first paint. Desktop keeps the theme's native options.
  // Only patch when the theme already initialized Slick — for single-image products
  // we let swapTo() initialize Slick with the correct options on first variant swap.
  function patchLive() {
    if (patchedOnce) return;
    patchedOnce = true;
    captureOpts();
    var t = getSwapThumbOpts(), m = getSwapMainOpts();
    var needPatch = isMobile() && (t && (t.variableWidth || t.focusOnSelect === false) || (m && m.asNavFor === null));
    if (needPatch && bothInitialized()) {
      try {
        if (slickOn($thumbs)) $thumbs.slick("unslick");
        if (slickOn($main)) $main.slick("unslick");
        if (t) $thumbs.slick(t);
        if (m) $main.slick(m);
      } catch (e) {}
    }
    armListeners();
    requestAnimationFrame(function () { updateCenter(); neutralizeCentered(); setActiveThumb(0); });
  }

  function onReady() {
    if (mainOptsRaw && thumbOptsRaw) patchLive();
    else { captureOpts(); if (mainOptsRaw && thumbOptsRaw) patchLive(); }
  }

  // ── Dimensions image + table ────────────────────────────────────────────────
  function applyDims(variantId) {
    if (variantId == null) return;

    // ── Gallery thumbnail-tail dimension image (stage + thumb visibility) ──
    // The tail "ruler" thumbnail (.taitiy-dim-thumb) opens a stage (.taitiy-dim-stage)
    // showing the full dimension diagram. It MUST switch with the variant too. The
    // inline taitiyDim() in product-media.liquid only listens to `variantChange`,
    // which this theme fires unreliably (it updates the master <select> via .val()),
    // so we drive it from the SAME reliable poller that powers the rest of applyDims.
    // Kept FIRST so it runs even when the about-dimensions map is absent.
    var mediaMapEl = document.getElementById("taitiy-media-dim-map");
    if (mediaMapEl) {
      var mm;
      try { mm = JSON.parse(mediaMapEl.textContent); } catch (e) { mm = null; }
      if (mm) {
        var me = mm[String(variantId)] || mm["default"];
        var stage = document.querySelector(".taitiy-dim-stage");
        var stageImg = stage ? stage.querySelector("img") : null;
        var thumb = document.querySelector(".taitiy-dim-thumb");
        if (me && me.img1200) {
          if (stageImg) {
            stageImg.src = me.img1200;
            stageImg.srcset = me.img1200 + " 1200w, " + (me.img600 || me.img1200) + " 600w";
          }
          if (thumb) thumb.style.display = "";
        } else {
          if (thumb) thumb.style.display = "none";
          if (stage && stage.classList.contains("is-open")) {
            stage.classList.remove("is-open");
            stage.setAttribute("aria-hidden", "true");
            if (thumb) thumb.classList.remove("is-active");
          }
        }
      }
    }

    // ── About-dimensions section image + table (product-about-dimensions.liquid) ──
    var mapEl = document.getElementById("product-dimensions-variant-map");
    if (mapEl) {
      var map;
      try { map = JSON.parse(mapEl.textContent); } catch (e) { map = null; }
      if (map) {
        var entry = map[String(variantId)] || map["default"];
        if (entry) {
          var imgEl = document.querySelector("[data-dim-image]");
          if (imgEl && entry.img) {
            imgEl.src = entry.img;
            imgEl.removeAttribute("width");
            imgEl.removeAttribute("height");
          }
          var tbody = document.querySelector("[data-dim-rows]");
          if (tbody && entry.rows && entry.rows.length) {
            tbody.innerHTML = "";
            entry.rows.forEach(function (r) {
              if (r && r.label && r.value) {
                var tr = document.createElement("tr");
                var td1 = document.createElement("td"); td1.textContent = r.label;
                var td2 = document.createElement("td"); td2.textContent = r.value;
                tr.appendChild(td1); tr.appendChild(td2);
                tbody.appendChild(tr);
              }
            });
          }
        }
      }
    }
  }

  // ── Apply a variant (id) ─────────────────────────────────────────────────────
  function doApply() {
    applyRAF = null;
    var id = pendingVariantId;
    if (!id) return;
    // Wait until we are ready: Slick captured (multi-image) OR defaults synthesized
    // (single-image), and the gallery DOM exists.
    if (!slickReady || !domReady() || !mainOptsRaw || !thumbOptsRaw) { arm(); return; }
    if (currentVariantId === id) { pendingVariantId = null; return; }
    try {
      var shown = showGallery(id);
      if (!shown && currentVariantId !== null) showDefault();
      pendingVariantId = null;
    } catch (e) {
      pendingVariantId = null;
    }
  }

  function arm() {
    if (readyRAF) return;
    var frames = 0;
    var MAX = 60;     // ~1s — ample time for the theme's async Slick init. If Slick
                      // never initializes (single-image products, or any product whose
                      // default gallery has <=1 thumb so the theme skips Slick), we
                      // synthesize sane defaults after this window so the variant
                      // gallery can swap in without an endless wait.
    function tick() {
      readyRAF = null;
      if (domReady()) {
        if (bothInitialized()) {
          // Theme initialized Slick → reuse its exact options.
          wasSlickInit = true;
          captureOpts();
          if (mainOptsRaw && thumbOptsRaw) {
            slickReady = true;
            onReady();
            scheduleApply();
            return;
          }
        } else if (frames >= MAX) {
          // Theme never initializes Slick. Synthesize sane defaults and proceed so
          // the variant gallery can swap in.
          captureOpts();
          ensureDefaults();
          slickReady = true;
          onReady();
          scheduleApply();
          return;
        }
      }
      if (frames++ < MAX) readyRAF = requestAnimationFrame(tick);
    }
    readyRAF = requestAnimationFrame(tick);
  }

  function scheduleApply() {
    if (!pendingVariantId) return;
    if (applyRAF) return;
    applyRAF = requestAnimationFrame(doApply);
  }

  // Central entry point. Dims apply immediately; gallery applies once ready.
  function applyById(id) {
    if (!id) return;
    id = String(id);
    if (id === lastAppliedId) return; // idempotent
    lastAppliedId = id;
    applyDims(id);
    pendingVariantId = id;
    if (slickReady && domReady() && mainOptsRaw && thumbOptsRaw) scheduleApply();
    else { slickReady = false; arm(); }
  }

  // ── Binding (runs on BOTH desktop and mobile) ───────────────────────────────
  function bind() {
    if (boundOnce) return;
    $root = $(ROOT).length ? $(ROOT) : $("body");
    $main = $root.find(MAIN).length ? $root.find(MAIN) : $(MAIN);
    $thumbs = $root.find(THUMBS).length ? $root.find(THUMBS) : $(THUMBS);
    $galleries = $root.find(GALLERIES).length ? $root.find(GALLERIES) : $(GALLERIES);
    $thumbsWrap = $root.find(THUMBS_WRAP).length ? $root.find(THUMBS_WRAP) : $(THUMBS_WRAP);
    if (!domReady()) return;

    boundOnce = true;
    captureOpts();
    if (bothInitialized() && mainOptsRaw && thumbOptsRaw) {
      wasSlickInit = true;
      slickReady = true;
      onReady();
    }

    $main.on("init", function () {
      captureOpts();
      if (bothInitialized() && mainOptsRaw && thumbOptsRaw) { wasSlickInit = true; slickReady = true; updateCenter(); neutralizeCentered(); }
    });
    $thumbs.on("init", function () {
      captureOpts();
      if (bothInitialized() && mainOptsRaw && thumbOptsRaw) { wasSlickInit = true; slickReady = true; updateCenter(); neutralizeCentered(); }
    });

    // ── Click guard: NEVER let gallery image links navigate away to the raw
    //    image URL (which "leaves the page"). This MUST run on ALL viewports:
    //      • On desktop the theme's own zoom/thumb-switch handlers normally call
    //        preventDefault, BUT they are bound to the ORIGINAL anchors and are
    //        lost whenever the variant gallery swaps the DOM (swapTo → $main.html
    //        / $thumbs.html). After such a swap, clicking the (new) main image or
    //        thumbnail anchor has no handler left → it falls back to its href and
    //        navigates away. This guard catches that case.
    //      • On mobile (and any case where theme.Product fails to initialize) the
    //        links would otherwise navigate too.
    //    We always preventDefault in the CAPTURE phase. The theme's own bubble-
    //    phase handlers still run (preventDefault does not stop other listeners),
    //    so the zoom modal / thumbnail switching keep working on the original
    //    anchors; for swapped anchors the navigation is simply neutralized.
    //    Scope is limited to the product gallery (.photos) so we never touch any
    //    other link on the page.
    document.addEventListener("click", function (e) {
      if (e.defaultPrevented) return;
      var t = e.target;
      if (!t || !t.closest) return;
      var a = t.closest(
        '.photos a.product-single__thumbnail, .photos a[itemprop="contentUrl"]'
      );
      if (a) e.preventDefault();
    }, true);

    // Primary: theme fires variantChange on the form container (bubbles to document).
    // e.variant is a jQuery extra property; fall back to the master select if absent.
    $(document).on("variantChange", function (e) {
      var id = (e && e.variant && e.variant.id != null) ? String(e.variant.id) : selectedId();
      applyById(id);
    });

    // Direct change on the option controls (selects / radios) AND on the master
    // select. When the user changes an option, the theme updates the master select
    // synchronously, so reading it here yields the new variant.
    $(document).on("change", ".single-option-selector, " + ID_SELECTOR, function () {
      applyById(selectedId());
    });

    // Resize: re-center; only re-bind from scratch if Slick was initialized by the
    // theme and then got destroyed (e.g. the theme re-rendered the gallery). For
    // single-image products (never Slick-initialized) we leave state alone.
    $(window).on("resize", function () {
      updateCenter();
      requestAnimationFrame(neutralizeCentered);
      if (wasSlickInit && !bothInitialized()) { slickReady = false; patchedOnce = false; arm(); }
    });

    // Initial paint for the pre-selected variant.
    applyById(selectedId());
    $(window).on("load", function () { applyById(selectedId()); });
  }

  // Lightweight poller: the theme updates the master <select> via .val() which does
  // NOT fire a `change` event, so the only reliable cross-method catch is to watch
  // the value on each frame (throttled to ~150ms). Cheap: one querySelector + a
  // string compare. Guarantees initial load AND every click/swatch/history switch.
  var lastPolled = null;
  var lastTick = 0;
  function startPoller() {
    if (pollRAF) return;
    function tick(ts) {
      pollRAF = requestAnimationFrame(tick);
      if (!lastTick || ts - lastTick >= 150) {
        lastTick = ts;
        var id = selectedId();
        if (id && id !== lastPolled) { lastPolled = id; applyById(id); }
      }
    }
    pollRAF = requestAnimationFrame(tick);
  }

  // The single source of truth for "which variant is selected right now".
  function selectedId() {
    var el = document.querySelector(ID_SELECTOR);
    if (el && el.value) return String(el.value);
    var m = location.search.match(/[?&]variant=(\d+)/);
    return m ? m[1] : null;
  }

  // ── Boot ─────────────────────────────────────────────────────────────────────
  var bindTries = 0;
  (function tryBind() {
    bind();
    if (!boundOnce && bindTries++ < 360) requestAnimationFrame(tryBind);
  })();

  startPoller();

  // Re-bind when the page content is swapped by the instant-nav (SPA) router.
  $(document).on("taitiy:page:changed", function () {
    if (readyRAF) { cancelAnimationFrame(readyRAF); readyRAF = null; }
    if (applyRAF) { cancelAnimationFrame(applyRAF); applyRAF = null; }
    mainOptsRaw = null;
    thumbOptsRaw = null;
    originalMain = null;
    originalThumbs = null;
    currentVariantId = null;
    pendingVariantId = null;
    lastAppliedId = null;
    lastPolled = null;
    slickReady = false;
    wasSlickInit = false;
    boundOnce = false;
    listenersArmed = false;
    patchedOnce = false;
    var t = 0;
    (function reBind() {
      bind();
      if (!boundOnce && t++ < 360) requestAnimationFrame(reBind);
    })();
  });
})();
; (function () {
  // taitiy: 点击视频缩略图/播放按钮 -> 全屏灯箱播放。
  function openVideoLightbox(vidId) {
    if (!vidId) return false;
    var hidden = document.querySelector('[data-media-type="video"][data-image-id="' + vidId + '"] .taitiy-video-src video');
    if (!hidden) return false;
    var v = hidden.cloneNode(true);
    v.removeAttribute("style");
    v.setAttribute("controls", "");
    v.setAttribute("autoplay", "");
    v.setAttribute("playsinline", "");
    v.setAttribute("webkit-playsinline", "");
    var lb = document.createElement("div");
    lb.className = "taitiy-video-lightbox";
    lb.style.cssText = "position:fixed;inset:0;background:rgba(0,0,0,.93);z-index:99999;display:flex;align-items:center;justify-content:center;";
    var wrap = document.createElement("div");
    wrap.style.cssText = "max-width:94vw;max-height:94vh;width:auto;";
    wrap.appendChild(v);
    var close = document.createElement("button");
    close.className = "taitiy-video-close";
    close.setAttribute("aria-label", "Close");
    close.innerHTML = "&times;";
    close.style.cssText = "position:absolute;top:16px;right:20px;width:46px;height:46px;border:none;border-radius:50%;background:rgba(255,255,255,.16);color:#fff;font-size:30px;line-height:1;cursor:pointer;z-index:1;";
    lb.appendChild(wrap); lb.appendChild(close);
    document.body.appendChild(lb);
    var prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function closeLb() {
      try { v.pause(); } catch (e) {}
      if (lb.parentNode) lb.parentNode.removeChild(lb);
      document.body.style.overflow = prevOverflow;
      if (document.fullscreenElement && document.exitFullscreen) { try { document.exitFullscreen(); } catch (e) {} }
    }
    close.addEventListener("click", function (e) { e.preventDefault(); closeLb(); });
    lb.addEventListener("click", function (e) { if (e.target === lb) closeLb(); });
    document.addEventListener("keydown", function esc(e) { if (e.key === "Escape") { closeLb(); document.removeEventListener("keydown", esc); } });
    var pr = v.play(); if (pr && pr.catch) pr.catch(function () {});
    if (lb.requestFullscreen) setTimeout(function () { try { lb.requestFullscreen(); } catch (e) {} }, 60);
    else if (lb.webkitRequestFullscreen) setTimeout(function () { try { lb.webkitRequestFullscreen(); } catch (e) {} }, 60);
    return true;
  }
  document.addEventListener("click", function (e) {
    var t = e.target; if (!t || !t.closest) return;
    var fig = t.closest('[data-media-type="video"]'); if (!fig) return;
    e.preventDefault(); e.stopPropagation();
    openVideoLightbox(fig.getAttribute("data-image-id"));
  });
})();
